/**
 * Customer cloud accounts attached to a tenant.
 *
 * A tenant is a customer; a cloud account is one place we may deploy for them.
 * The two are separate because a customer routinely has more than one — a trial
 * account, then production, sometimes one per region or business unit.
 */
/**
 * Clouds this can register an account in.
 *
 * A union of one, deliberately. Azure and GCP are planned, and expressing the
 * dimension now means every place that branches on it must handle a new member
 * to compile. The prototype had the opposite: a `platform` string threaded
 * everywhere, switches that fell through to `require`-ing modules that did not
 * exist, and — the bug this criterion names — **AWS role ARNs synthesised
 * regardless of which platform was asked for.** A string cannot fail to compile;
 * this can.
 */
export const CLOUDS = ['aws'];
export const isCloud = (value) => CLOUDS.includes(value);
/** Exactly twelve digits. Anything else can never be assumed into. */
const AWS_ACCOUNT_ID = /^\d{12}$/;
export class InvalidCloudAccountError extends Error {
}
/**
 * The identifier a cloud account is stored and addressed by.
 *
 * Cloud-qualified, so an AWS account and a hypothetical Azure subscription that
 * happened to share digits could never collide in one tenant's partition.
 */
export const cloudAccountKey = (account) => {
    switch (account.cloud) {
        case 'aws':
            return `aws:${account.awsAccountId}`;
    }
};
/**
 * The cloud an account key names.
 *
 * Keys are cloud-qualified by construction (`aws:123456789012`), so the cloud is
 * readable from the key without a store lookup — which is what lets an endpoint
 * holding only a key pick an adapter. Returns undefined for anything that is not
 * a key this system issued, and callers treat that as "no such account" rather
 * than as a malformed request: whether a key is well-formed is not something to
 * confirm to a caller who cannot see the account.
 */
export const cloudFromAccountKey = (accountKey) => {
    const separator = accountKey.indexOf(':');
    if (separator <= 0)
        return undefined;
    const cloud = accountKey.slice(0, separator);
    return isCloud(cloud) ? cloud : undefined;
};
/**
 * The AWS account id a key names, or undefined if it does not name one.
 *
 * Undefined covers both "this is another cloud's key" and "this is not a key we
 * issued", because a caller that wants the twelve digits can do nothing useful
 * with either. Validated rather than sliced: a key is data that has been through
 * storage, and a caller building an ARN or a bucket name out of a malformed one
 * would address something it did not mean to.
 */
export const awsAccountIdFromAccountKey = (accountKey) => {
    if (cloudFromAccountKey(accountKey) !== 'aws')
        return undefined;
    const awsAccountId = accountKey.slice('aws:'.length);
    return AWS_ACCOUNT_ID.test(awsAccountId) ? awsAccountId : undefined;
};
/**
 * Validates registration input.
 *
 * Rejecting a malformed account id here means the failure is a 400 at
 * registration rather than an `AccessDenied` from `sts:AssumeRole` days later,
 * when nobody remembers typing it.
 */
export const validateRegistration = (input) => {
    if (typeof input.cloud !== 'string' || !isCloud(input.cloud)) {
        throw new InvalidCloudAccountError(`cloud must be one of: ${CLOUDS.join(', ')}`);
    }
    const label = typeof input.label === 'string' ? input.label.trim() : '';
    if (label === '' || label.length > 100) {
        throw new InvalidCloudAccountError('label must be 1 to 100 characters');
    }
    switch (input.cloud) {
        case 'aws': {
            const awsAccountId = typeof input.awsAccountId === 'string' ? input.awsAccountId.trim() : '';
            if (!AWS_ACCOUNT_ID.test(awsAccountId)) {
                throw new InvalidCloudAccountError('awsAccountId must be exactly 12 digits');
            }
            return { cloud: 'aws', awsAccountId, label };
        }
    }
};
/**
 * The ARN of the role we assume in a customer's AWS account.
 *
 * Only reachable from the AWS branch, which is the whole point of the
 * discriminated union: there is no way to ask for the ARN of a non-AWS account,
 * so the prototype's bug cannot be written here.
 */
export const customerRoleArn = (account, roleName) => {
    if (!AWS_ACCOUNT_ID.test(account.awsAccountId)) {
        throw new InvalidCloudAccountError(`not an AWS account id: ${account.awsAccountId}`);
    }
    return `arn:aws:iam::${account.awsAccountId}:role/${roleName}`;
};
/**
 * The role the control plane assumes in a customer's account.
 *
 * The *diagnostics* role, not a deploy role. The bootstrap template also creates
 * `QrveyDeployExecRole`, which is broad and assumable only by the in-account
 * Terraform runner — the control plane is deliberately not a trusted principal
 * of it, so a compromise here cannot become write access to a customer's
 * infrastructure. Naming the deploy role here would be a standing invitation to
 * try to assume it.
 */
export { DIAGNOSTICS_ROLE_NAME as CONTROL_PLANE_ROLE_NAME } from '../bootstrap/template.js';
//# sourceMappingURL=cloud-account.js.map